import { brand, generatedAvatarDataUrl } from '@metorial/brand';
import { renderWithLoader } from '@metorial/data-hooks';
import { Paths } from '@metorial/frontend-config';
import { ContentLayout, PageHeader } from '@metorial/layout';
import {
  MetorialEnterpriseWindow,
  useCurrentInstance,
  useDashboardFlags,
  usePortals,
  useUser
} from '@metorial/state';
import { Avatar, Button, Entity, Spacer, Text, theme } from '@metorial/ui';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { HomeProvidersTable } from '../scenes/providers/homeTable';

let WorkforceGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 400px), 1fr));
`;

let WorkforceLink = styled(Link)`
  color: inherit;
  min-width: 0;
  text-decoration: none;

  & > div {
    min-width: 0;
  }
`;

let IntegrateCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid ${theme.colors.gray300};
  background: ${theme.colors.background};

  @media (max-width: 640px) {
    flex-wrap: wrap;
  }
`;

let IntegrateText = styled.div`
  flex: 1 1 260px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

declare global {
  interface Window {
    metorial_enterprise?: MetorialEnterpriseWindow;
  }
}

export let ProjectHomePage = () => {
  let instance = useCurrentInstance();
  let user = useUser();
  let flags = useDashboardFlags();
  let portalsEnabled = !!flags.data?.flags['portals-access'];

  let portals = usePortals(portalsEnabled ? instance.data?.id : null);

  let pathItems = [
    instance.data?.organization,
    instance.data?.project,
    instance.data
  ] as const;

  return (
    <ContentLayout variant="medium2">
      <Spacer height={50} />

      {user.data && (
        <PageHeader
          title={`Welcome to ${brand.name}, ${user.data?.name}!`}
          description="It's a good day to build something amazing. Here are your resource at a glance."
        />
      )}

      {renderWithLoader({ instance })(() => (
        <>
          <IntegrateCard>
            <IntegrateText>
              <Text size="3" weight="strong">
                Integrate {brand.name}
              </Text>
              <Text size="2" color="gray600">
                Learn how to integrate {brand.name} with your AI app. We have guides for various
                programming languages and frameworks.
              </Text>
            </IntegrateText>

            {/* No hosted docs: the quickstart lives on Developer › API Settings. */}
            <Link to={Paths.instance.developer(...pathItems, 'api')}>
              <Button size="2" as="span">
                Read the Docs
              </Button>
            </Link>
          </IntegrateCard>

          <Spacer height={30} />

          {portalsEnabled && !!portals.data?.items.length && (
            <>
              <PageHeader
                title={`${brand.name} Workforce`}
                description={`Give your team governed access to integrations on ${brand.name}.`}
                size="5"
              />

              <WorkforceGrid>
                {portals.data?.items.map(p => (
                  <WorkforceLink to={Paths.instance.portal(...pathItems, p.id)} key={p.id}>
                    <Entity.Wrapper>
                      <Entity.Content>
                        <Entity.Field
                          title={p.name}
                          prefix={
                            <Avatar
                              entity={{
                                ...p,
                                imageUrl: generatedAvatarDataUrl(p.id)
                              }}
                              size={30}
                            />
                          }
                        />

                        <Entity.Field right title="Actions">
                          <Button size="2" as="span">
                            Manage Portal
                          </Button>
                        </Entity.Field>
                      </Entity.Content>
                    </Entity.Wrapper>
                  </WorkforceLink>
                ))}
              </WorkforceGrid>

              <Spacer height={25} />
            </>
          )}

          <PageHeader
            title="Integration Providers"
            description={`Providers you have used recently or ones that are popular on ${brand.name}.`}
            size="5"
            actions={
              <Link to={Paths.instance.providers(...pathItems)}>
                <Button size="2" as="span" variant="outline">
                  View All Providers
                </Button>
              </Link>
            }
          />

          <HomeProvidersTable limit={18} orderByUse="last_deployment_at" orderByRank />
        </>
      ))}
    </ContentLayout>
  );
};
